import { RarumUserProfile } from 'core/logic/user/user.types'
import { RootState } from 'core/modules/redux'
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FirebaseReducer, useFirebase } from 'react-redux-firebase'
import { actions as userActions } from 'core/logic/user/user.reducer'
import { GalleryType } from 'core/logic/gallery/gallery.types'
import { get, has, omit, pick, set } from 'lodash'
import { DeepPartial } from '@onepercentio/one-ui/types'
import useLanguage from 'openspace/hooks/useLanguage'
import { fromProfile, toV2 } from 'utility/address'
import useAsyncControl from '@onepercentio/one-ui/hooks/useAsyncControl'
import useInitialization from 'utility/hooks/useInitialization'
import {
  addAddress,
  getAddresses,
  UserAddress,
} from 'core/modules/firebase/service/user'
import { AddressSchema } from '@onepercentio/rarum-types/_shared'
import { useWebviewAuthentication } from './WebviewIntegration'
import { setUserForLogs } from './ErrorReporting'

export type UserContextShape = ClickActions<
  'remove' | 'logout' | 'cancelAccount'
> & {
  isEmpty: boolean
  detail: FirebaseReducer.AuthState | undefined
  profile: FirebaseReducer.Profile<RarumUserProfile> | undefined
  setAcceptDataUsage: (accept: boolean, gallery?: GalleryType) => Promise<void>
  updateProfile: (
    rarumUserProfile: DeepPartial<RarumUserProfile>
  ) => Promise<void>
  updateLastAddress: (address: UserAddress) => Promise<void>
  isLoggedIn: boolean
  wallets: string[]
  addresses: UserAddress[] | undefined
  updateAddresses: (newAddresses: UserAddress[]) => void
}
export const UserContext = createContext<UserContextShape>(null as any)

export default function UserProvider({ children }: PropsWithChildren<{}>) {
  const [addresses, setAddresses] = useState<UserAddress[]>()
  const { updateProfile } = useFirebase()
  const dispatch = useDispatch()
  useWebviewAuthentication()
  const state = useSelector<
    RootState,
    {
      auth?: FirebaseReducer.AuthState
      profile?: FirebaseReducer.Profile<RarumUserProfile>
      isEmpty: boolean
    }
  >(({ firebase: { auth, profile } }) => {
    const { isLoaded, isEmpty } = auth
    if (isLoaded && !isEmpty) {
      return { auth, profile, isEmpty }
    }
    return { isEmpty }
  })
  const setAcceptDataUsage = useCallback<
    UserContextShape['setAcceptDataUsage']
  >(
    (accepted, gallery) => {
      const profile = state.profile
      const userTenantConfig: NonNullable<
        NonNullable<RarumUserProfile['personalData']>['tenants']
      >[string] =
        profile?.personalData?.tenants &&
        profile?.personalData?.tenants[process.env.REACT_APP_TENANT]
          ? profile.personalData.tenants[process.env.REACT_APP_TENANT]
          : { dataConsent: [], galleries: {} }

      if (!gallery) {
        userTenantConfig.dataConsent.push({
          value: accepted,
          created: new Date(), // Important to collect user's date and time
        })
      } else {
        const dataConsentGalleryPath = `galleries.${gallery.id}.dataConsent`
        if (!has(userTenantConfig, dataConsentGalleryPath))
          set(userTenantConfig, `galleries.${gallery.id}.dataConsent`, [])
        const galleryDataConsents = get(
          userTenantConfig,
          dataConsentGalleryPath
        ) as typeof userTenantConfig.dataConsent
        galleryDataConsents.push({
          created: new Date(),
          value: accepted,
        })
      }
      return updateProfile({
        personalData: {
          ...profile?.personalData,
          tenants: {
            ...profile?.personalData?.tenants,
            [process.env.REACT_APP_TENANT!]: userTenantConfig,
          },
        },
      })
    },
    [state.profile, updateProfile]
  )
  const isLoggedIn = useMemo(() => {
    return !!state.auth?.uid
  }, [state.profile])

  const { profile: _profile } = state
  const profile = useMemo(() => {
    if (!_profile) return _profile
    return {
      ..._profile,
      wallet: _profile.wallet?.toLowerCase(),
      internalWallet: _profile.internalWallet?.toLowerCase(),
    }
  }, [_profile])
  const wallets = useMemo(
    () =>
      [profile?.internalWallet, profile?.wallet].filter(Boolean) as string[],
    [profile?.internalWallet, profile?.wallet]
  )
  const updateLastAddress = useCallback<UserContextShape['updateLastAddress']>(
    async (add) => {
      if (add.id !== profile?.personalData?.lastUsedAddress)
        await updateProfile({
          personalData: {
            lastUsedAddress: add.id,
            ...pick(
              add,
              ...([
                'city',
                'complement',
                'country',
                'neighborhood',
                'number',
                'state',
                'street',
                'zipCode',
              ] as (keyof UserAddress)[])
            ),
          },
        })
    },
    [profile]
  )

  const language = useLanguage()
  useEffect(() => {
    if (profile && profile.isLoaded)
      if (language !== profile.personalData?.prefferedLanguage) {
        updateProfile({
          personalData: {
            ...profile?.personalData,
            prefferedLanguage: language,
          },
        })
      }
  }, [language, !!profile])

  useEffect(() => {
    setUserForLogs(state.auth?.uid)
  }, [state.auth?.uid])

  useAddressMigration(profile, setAddresses)

  const cancelAccount = () => {}
  return (
    <UserContext.Provider
      value={{
        updateAddresses: setAddresses,
        addresses,
        remove: () => dispatch(userActions.remove()),
        logout: () => dispatch(userActions.logout()),
        isEmpty: state.isEmpty,
        detail: state.auth,
        profile,
        setAcceptDataUsage,
        cancelAccount,
        isLoggedIn,
        wallets,
        updateProfile,
        updateLastAddress,
      }}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  return useContext(UserContext)
}

export function useAddresses() {
  const { addresses, updateAddresses } = useUser()
  const control = useAsyncControl({ getAddresses })
  const refreshAddresses = useCallback(() => {
    control.getAddresses().then(updateAddresses)
  }, [])
  useEffect(() => {
    refreshAddresses()
  }, [])

  return {
    addresses,
    refreshAddresses,
    ...control,
  }
}

export function useAddressManagement() {
  const addressActions = useAsyncControl({
    async createAddress(address: AddressSchema) {
      return await addAddress(address)
    },
  })
  return addressActions
}

function useAddressMigration(
  profile: ReturnType<typeof useUser>['profile'] = undefined,
  setAddresses: UserContextShape['updateAddresses']
) {
  const { createAddress } = useAddressManagement()
  const { updateProfile } = useFirebase()
  useEffect(() => {
    /** There is an address but it's old format */
    if (
      profile?.isLoaded &&
      !!profile?.personalData?.address &&
      !profile.personalData.lastUsedAddress
    ) {
      createAddress(toV2(fromProfile(profile))).then((id) => {
        updateProfile({
          personalData: {
            lastUsedAddress: id,
          },
        }).then(() => getAddresses().then(setAddresses))
      })
    }
  }, [profile?.isLoaded])
}
